import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { formatMessage } from "lib/globalize-util";
import Sort from "components/sort-picker";
import { SNACK_ID } from "components/product/quick-add/quick-add-popup";
import { IMMEDIATE, PREBOOK } from "constants/delivery-window-types";
import { addLineItem } from "actions/working-order/line-items";
import { InsightsActions } from "constants/analytics";
import me from "models/me";
import { CART_ITEM_REMOVE } from "constants/action-types";
import { IAddLineItemPayload } from "types/working-order/line-items";
import theme from "styles/theme";
import type { IStore } from "reducers";
import type { IState as IScope } from "reducers/scope";
import type { ProductStyleInsights } from "models/insights/product-style-insights";
import type { IItemRecord } from "records/item/item";
import { TopStyleInsightsProduct } from "./insights-product";
import { InlineLoading } from "components/content-loading";
import { removeSizeInsightsProduct } from "actions/insights-actions";

const useStyles = makeStyles({
    container: {
        fontSize: "1.3rem",
        color: theme.palette.textColor,
    },
    title: {
        fontWeight: "bold",
    },
    formControl: {
        width: "100%",
    },
    select: {
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#2dbeb4",
        },
    },
    selectLabel: {
        paddingTop: "20px",
        paddingBottom: "5px",
    },
    root: {
        minWidth: 275,
    },
    bullet: {
        display: "inline-block",
        margin: "0 2px",
        transform: "scale(0.8)",
    },
    pos: {
        marginBottom: 12,
    },
    paper: {
        textAlign: "center",
    },
    unavailableText: {
        paddingTop: "20px",
    },
});

interface OwnProps {
    isCartPage?: boolean;
    onClickOnProduct: (product: Pick<IItemRecord, "_id" | "name">) => void;
}

interface StateProps {
    scope: IScope;
    cart: any;
    immediateProducts: ProductStyleInsights[];
    prebookProducts: ProductStyleInsights[];
    isLoading: boolean;
}

interface DispatchProps {
    onAddLineItem: (item: IAddLineItemPayload, fromInsightsWoPanel: boolean, brandId: string) => void;
    onRemoveProductFromCart: (productId: string) => void;
}

const OrderInsightsProductStyles: React.FC<StateProps & DispatchProps & OwnProps> = ({
    scope,
    cart,
    onAddLineItem,
    onRemoveProductFromCart,
    immediateProducts,
    prebookProducts,
    onClickOnProduct,
    isLoading,
    isCartPage,
}) => {
    const classes = useStyles();
    const [productType, setProductType] = useState(IMMEDIATE);
    const [placeOrderDisabled, setPlaceOrderDisabled] = useState(false);

    useEffect(() => {
        if (immediateProducts?.length == 0 && prebookProducts?.length > 0) {
            setProductType(PREBOOK);
        }
    }, [immediateProducts, prebookProducts]);

    const trackSelectEvent = (productType) => {
        let eventType = null;
        if (productType === IMMEDIATE) {
            eventType = InsightsActions.selectedImmediateProductInsights;
        } else if (productType === PREBOOK) {
            eventType = InsightsActions.selectedPrebookProductInsights;
        }

        if (eventType) {
            global.analytics.track(eventType, {
                brand_id: scope.brand._id,
                accountType: me.accountType,
            });
        }
    };

    const trackOpenProductSelect = () => {
        global.analytics.track(InsightsActions.selectProductTypeClick, {
            brand_id: scope.brand._id,
            accountType: me.accountType,
        });
    };

    const handleChange = (_, __, name) => {
        setProductType(name);

        trackSelectEvent(name);
    };

    const sortChoices = [
        [IMMEDIATE, formatMessage("Immediate products (last six months)")],
        [PREBOOK, formatMessage("Prebook products (last three months)")],
    ];

    if (isLoading) {
        return <InlineLoading />;
    }

    const unavailableImmediateProductsText = formatMessage(
        "No Order Trends due to visibility rules or no Immediate USD orders for this brand within the last six months."
    );

    const unavailablePrebookProductsText = formatMessage(
        "No Order Trends due to visibility rules or no Prebook USD orders for this brand within the last three months."
    );

    const getUnavailableText = (productType) => {
        return productType === IMMEDIATE ? unavailableImmediateProductsText : unavailablePrebookProductsText;
    };

    const renderProducts = (products, productType, unavailableText) => {
        return (
            <div>
                {products.map((product) => (
                    <div key={`${product.product_id}-${product.rank[productType]}`}>
                        <TopStyleInsightsProduct
                            product={product}
                            scope={scope}
                            rank={product.rank[productType]}
                            cart={cart}
                            onAddLineItem={(item: IAddLineItemPayload, fromInsightsWoPanel: boolean) =>
                                onAddLineItem(item, fromInsightsWoPanel, scope.brand._id)
                            }
                            onRemoveProductFromCart={onRemoveProductFromCart}
                            onClick={() =>
                                onClickOnProduct({
                                    _id: product.product_id,
                                    name: product.name,
                                })
                            }
                            isCartPage={isCartPage}
                            placeOrderDisabled={placeOrderDisabled}
                            setPlaceOrderDisabled={setPlaceOrderDisabled}
                            productType={productType}
                        />
                    </div>
                ))}
                {products.length === 0 && <div className={classes.unavailableText}>{unavailableText}</div>}
            </div>
        );
    };

    return (
        <div className={classes.container}>
            <div className={classes.title}>{formatMessage("Top ordered products")}</div>

            {immediateProducts.length === 0 && prebookProducts.length === 0 && (
                <div className={classes.unavailableText}>
                    {formatMessage(
                        "No Order Trends due to visibility rules or no USD orders for this brand within the last six months (Immediate) or three months (Prebook)."
                    )}
                </div>
            )}

            {(immediateProducts.length > 0 || prebookProducts.length > 0) && (
                <div>
                    <div className={classes.selectLabel}>
                        {formatMessage("Based on all of this brand’s USD orders for:")}
                    </div>

                    <Sort
                        value={productType}
                        choices={sortChoices}
                        onChange={handleChange}
                        onClick={trackOpenProductSelect}
                        style={{ width: "100%", ".dropdownContainer": { width: "100%" } }}
                    />

                    <div>
                        {productType === IMMEDIATE &&
                            renderProducts(immediateProducts, IMMEDIATE, getUnavailableText(IMMEDIATE))}

                        {productType === PREBOOK &&
                            renderProducts(prebookProducts, PREBOOK, getUnavailableText(PREBOOK))}
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state: IStore) => {
    const orderProductsByRankAndType = (type: "immediate" | "prebook") => {
        return state.insights.productStyles?.[type]?.sort((a, b) => a.rank[type] - b.rank[type]) || [];
    };

    return {
        scope: state.scope,
        cart: state.cart,
        immediateProducts: orderProductsByRankAndType(IMMEDIATE),
        prebookProducts: orderProductsByRankAndType(PREBOOK),
        isLoading: state.insights.isLoading.productStyles,
    };
};

const mapDispatchToProps = (dispatch, ownProps: OwnProps) => {
    return {
        onAddLineItem: (item: IAddLineItemPayload, fromInsightsWoPanel: boolean, brandId: string) => {
            dispatch(
                addLineItem(item, {
                    notifyCart: true,
                    updateUI: ownProps.isCartPage,
                    snackbar: {
                        message: formatMessage("Product added to cart"),
                        id: SNACK_ID,
                        shouldUseLatestSnack: true,
                        ...(!ownProps.isCartPage && {
                            customAction: {
                                type: "insights-add",
                                data: {
                                    brandId: brandId,
                                    itemId: item.body.item_id,
                                },
                            },
                        }),
                        messageStyle: {
                            fontSize: 14,
                            fontFamily: theme.fontFamily,
                        },
                    },
                    fromInsightsWoPanel,
                })
            );
        },
        onRemoveProductFromCart: (productId: string) => {
            dispatch({
                type: CART_ITEM_REMOVE,
                payload: productId,
            });

            dispatch(removeSizeInsightsProduct(productId));
        },
    };
};

export const OrderInsightsProductStylesContainer = connect<StateProps, DispatchProps, OwnProps>(
    mapStateToProps,
    mapDispatchToProps
)(OrderInsightsProductStyles);
export default OrderInsightsProductStylesContainer;
